import _defineProperty from "/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import { unit } from '@ant-design/cssinjs';
import { resetIcon } from '../../style';
import { mergeToken } from '../../theme/internal';
/**
 * Get multiple selector needed style. The calculation:
 *
 * ContainerPadding = BasePadding - ItemMargin
 *
 * Border:                    ╔═══════════════════════════╗                 ┬
 * ContainerPadding:          ║                           ║                 │
 *                            ╟───────────────────────────╢     ┬           │
 * Item Margin:               ║                           ║     │           │
 *                            ║             ┌──────────┐  ║     │           │
 * Item(multipleItemHeight):  ║ BasePadding │   Item   │  ║  Overflow  Container(ControlHeight)
 *                            ║             └──────────┘  ║     │           │
 * Item Margin:               ║                           ║     │           │
 *                            ╟───────────────────────────╢     ┴           │
 * ContainerPadding:          ║                           ║                 │
 * Border:                    ╚═══════════════════════════╝                 ┴
 */

export var getMultipleSelectorUnit = function getMultipleSelectorUnit(token) {
  var multipleSelectItemHeight = token.multipleSelectItemHeight,
      paddingXXS = token.paddingXXS,
      lineWidth = token.lineWidth,
      INTERNAL_FIXED_ITEM_MARGIN = token.INTERNAL_FIXED_ITEM_MARGIN;
  var basePadding = token.max(token.calc(paddingXXS).sub(lineWidth).equal(), 0);
  var containerPadding = token.max(token.calc(basePadding).sub(INTERNAL_FIXED_ITEM_MARGIN).equal(), 0);
  return {
    basePadding: basePadding,
    containerPadding: containerPadding,
    itemHeight: unit(multipleSelectItemHeight),
    itemLineHeight: unit(token.calc(multipleSelectItemHeight).sub(token.calc(token.lineWidth).mul(2)).equal())
  };
};

var getSelectItemStyle = function getSelectItemStyle(token) {
  var multipleSelectItemHeight = token.multipleSelectItemHeight,
      selectHeight = token.selectHeight,
      lineWidth = token.lineWidth;
  var selectItemDist = token.calc(selectHeight).sub(multipleSelectItemHeight).div(2).sub(lineWidth).equal();
  return selectItemDist;
};
/**
 * Get the `rc-overflow` needed style.
 * It's a share style which means not affected by `size`.
 */


export var genOverflowStyle = function genOverflowStyle(token) {
  var _Object$assign, _$concat;

  var componentCls = token.componentCls,
      iconCls = token.iconCls,
      borderRadiusSM = token.borderRadiusSM,
      motionDurationSlow = token.motionDurationSlow,
      paddingXS = token.paddingXS,
      multipleItemColorDisabled = token.multipleItemColorDisabled,
      multipleItemBorderColorDisabled = token.multipleItemBorderColorDisabled,
      colorIcon = token.colorIcon,
      colorIconHover = token.colorIconHover,
      INTERNAL_FIXED_ITEM_MARGIN = token.INTERNAL_FIXED_ITEM_MARGIN;
  var selectOverflowPrefixCls = "".concat(componentCls, "-selection-overflow");
  return _defineProperty({}, selectOverflowPrefixCls, _defineProperty({
    position: 'relative',
    display: 'flex',
    flex: 'auto',
    flexWrap: 'wrap',
    maxWidth: '100%',
    '&-item': {
      flex: 'none',
      alignSelf: 'center',
      maxWidth: '100%',
      display: 'inline-flex'
    }
  }, "".concat(componentCls, "-selection-item"), (_$concat = {
    display: 'flex',
    alignSelf: 'center',
    flex: 'none',
    boxSizing: 'border-box',
    maxWidth: '100%',
    marginBlock: INTERNAL_FIXED_ITEM_MARGIN,
    borderRadius: borderRadiusSM,
    cursor: 'default',
    transition: "font-size ".concat(motionDurationSlow, ", line-height ").concat(motionDurationSlow, ", height ").concat(motionDurationSlow),
    marginInlineEnd: token.calc(INTERNAL_FIXED_ITEM_MARGIN).mul(2).equal(),
    paddingInlineStart: paddingXS,
    paddingInlineEnd: token.calc(paddingXS).div(2).equal()
  }, _defineProperty(_$concat, "".concat(componentCls, "-disabled&"), {
    color: multipleItemColorDisabled,
    borderColor: multipleItemBorderColorDisabled,
    cursor: 'not-allowed'
  }), _defineProperty(_$concat, '&-content', {
    display: 'inline-block',
    marginInlineEnd: token.calc(paddingXS).div(2).equal(),
    overflow: 'hidden',
    whiteSpace: 'pre',
    // fix whitespace wrapping. custom tags display all whitespace within.
    textOverflow: 'ellipsis'
  }), _defineProperty(_$concat, '&-remove', Object.assign(Object.assign({}, resetIcon()), (_Object$assign = {
    display: 'inline-flex',
    alignItems: 'center',
    color: colorIcon,
    fontWeight: 'bold',
    fontSize: 10,
    lineHeight: 'inherit',
    cursor: 'pointer'
  }, _defineProperty(_Object$assign, "> ".concat(iconCls), {
    verticalAlign: '-0.2em'
  }), _defineProperty(_Object$assign, '&:hover', {
    color: colorIconHover
  }), _Object$assign))), _$concat)));
};

var genSelectionStyle = function genSelectionStyle(token, suffix) {
  var _$concat2, _$concat3, _Object$assign2;

  var componentCls = token.componentCls,
      INTERNAL_FIXED_ITEM_MARGIN = token.INTERNAL_FIXED_ITEM_MARGIN;
  var selectOverflowPrefixCls = "".concat(componentCls, "-selection-overflow");
  var selectItemHeight = token.multipleSelectItemHeight;
  var selectItemDist = getSelectItemStyle(token);
  var suffixCls = suffix ? "".concat(componentCls, "-").concat(suffix) : '';
  var multipleSelectorUnit = getMultipleSelectorUnit(token);
  return _defineProperty({}, "".concat(componentCls, "-multiple").concat(suffixCls), Object.assign(Object.assign({}, genOverflowStyle(token)), (_Object$assign2 = {}, _defineProperty(_Object$assign2, "".concat(componentCls, "-selector"), (_$concat2 = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    height: '100%',
    // Multiple is little different that horizontal is follow the vertical
    paddingInline: multipleSelectorUnit.basePadding,
    paddingBlock: multipleSelectorUnit.containerPadding,
    borderRadius: token.borderRadius
  }, _defineProperty(_$concat2, "".concat(componentCls, "-disabled&"), {
    background: token.multipleSelectorBgDisabled,
    cursor: 'not-allowed'
  }), _defineProperty(_$concat2, '&:after', {
    display: 'inline-block',
    width: 0,
    margin: "".concat(unit(INTERNAL_FIXED_ITEM_MARGIN), " 0"),
    lineHeight: unit(selectItemHeight),
    visibility: 'hidden',
    content: '"\\a0"'
  }), _$concat2)), _defineProperty(_Object$assign2, "".concat(componentCls, "-selection-item"), {
    height: multipleSelectorUnit.itemHeight,
    lineHeight: unit(multipleSelectorUnit.itemLineHeight)
  }), _defineProperty(_Object$assign2, "".concat(selectOverflowPrefixCls, "-item + ").concat(selectOverflowPrefixCls, "-item"), _defineProperty({}, "".concat(componentCls, "-selection-search"), {
    marginInlineStart: 0
  })), _defineProperty(_Object$assign2, "".concat(selectOverflowPrefixCls, "-item-suffix"), {
    height: '100%'
  }), _defineProperty(_Object$assign2, "".concat(componentCls, "-selection-search"), (_$concat3 = {
    display: 'inline-flex',
    position: 'relative',
    maxWidth: '100%',
    marginInlineStart: token.calc(token.inputPaddingHorizontalBase).sub(selectItemDist).equal()
  }, _defineProperty(_$concat3, "\n          &-input,\n          &-mirror\n        ", {
    height: selectItemHeight,
    fontFamily: token.fontFamily,
    lineHeight: unit(selectItemHeight),
    transition: "all ".concat(token.motionDurationSlow)
  }), _defineProperty(_$concat3, '&-input', {
    width: '100%',
    minWidth: 4.1 // fix search cursor missing

  }), _defineProperty(_$concat3, '&-mirror', {
    position: 'absolute',
    top: 0,
    insetInlineStart: 0,
    insetInlineEnd: 'auto',
    zIndex: 999,
    whiteSpace: 'pre',
    // fix whitespace wrapping caused width calculation bug
    visibility: 'hidden'
  }), _$concat3)), _defineProperty(_Object$assign2, "".concat(componentCls, "-selection-placeholder"), {
    position: 'absolute',
    top: '50%',
    insetInlineStart: token.inputPaddingHorizontalBase,
    insetInlineEnd: token.inputPaddingHorizontalBase,
    transform: 'translateY(-50%)',
    transition: "all ".concat(token.motionDurationSlow)
  }), _Object$assign2)));
};

function genSizeStyle(token, suffix) {
  var _$concat$concat2;

  var componentCls = token.componentCls;
  var suffixCls = suffix ? "".concat(componentCls, "-").concat(suffix) : '';

  var rawStyle = _defineProperty({}, "".concat(componentCls, "-multiple").concat(suffixCls), (_$concat$concat2 = {
    fontSize: token.fontSize
  }, _defineProperty(_$concat$concat2, "".concat(componentCls, "-selector"), _defineProperty({}, "".concat(componentCls, "-show-search&"), {
    cursor: 'text'
  })), _defineProperty(_$concat$concat2, "\n        &".concat(componentCls, "-show-arrow ").concat(componentCls, "-selector,\n        &").concat(componentCls, "-allow-clear ").concat(componentCls, "-selector\n      "), {
    paddingInlineEnd: token.calc(token.fontSizeIcon).add(token.controlPaddingHorizontal).equal()
  }), _$concat$concat2));

  return [genSelectionStyle(token, suffix), rawStyle];
}

var genMultipleStyle = function genMultipleStyle(token) {
  var _$concat$concat3;

  var componentCls = token.componentCls;
  var smallToken = mergeToken(token, {
    selectHeight: token.controlHeightSM,
    multipleSelectItemHeight: token.multipleItemHeightSM,
    borderRadius: token.borderRadiusSM,
    borderRadiusSM: token.borderRadiusXS
  });
  var largeToken = mergeToken(token, {
    fontSize: token.fontSizeLG,
    selectHeight: token.controlHeightLG,
    multipleSelectItemHeight: token.multipleItemHeightLG,
    borderRadius: token.borderRadiusLG,
    borderRadiusSM: token.borderRadius
  });
  return [genSizeStyle(token), // ======================== Small ========================
  genSizeStyle(smallToken, 'sm'), // Padding
  _defineProperty({}, "".concat(componentCls, "-multiple").concat(componentCls, "-sm"), (_$concat$concat3 = {}, _defineProperty(_$concat$concat3, "".concat(componentCls, "-selection-placeholder"), {
    insetInline: token.calc(token.controlPaddingHorizontalSM).sub(token.lineWidth).equal()
  }), _defineProperty(_$concat$concat3, "".concat(componentCls, "-selection-search"), {
    marginInlineStart: 2 // Magic Number

  }), _$concat$concat3)), // ======================== Large ========================
  genSizeStyle(largeToken, 'lg')];
};

export default genMultipleStyle;